interface DecimalValue {
  coefficient: bigint
  scale: number
}

export interface PayoutFeeEstimate {
  percentageFee: string
  totalFee: string
  netAmount: string
}

const DECIMAL_PATTERN = /^[+-]?\d+(?:\.\d+)?$/

function powerOfTen(exponent: number): bigint {
  return 10n ** BigInt(exponent)
}

function parseDecimal(value: string): DecimalValue {
  const trimmed = value.trim()
  if (!DECIMAL_PATTERN.test(trimmed)) {
    throw new Error(`Invalid decimal string: ${value}`)
  }

  const negative = trimmed.startsWith('-')
  const unsigned = trimmed.replace(/^[+-]/, '')
  const [whole, fraction = ''] = unsigned.split('.')
  const digits = `${whole}${fraction}`.replace(/^0+(?=\d)/, '')
  const coefficient = BigInt(digits)
  return {
    coefficient: negative ? -coefficient : coefficient,
    scale: fraction.length,
  }
}

function toDecimalString({ coefficient, scale }: DecimalValue): string {
  const negative = coefficient < 0n
  const digits = (negative ? -coefficient : coefficient).toString().padStart(scale + 1, '0')
  if (scale === 0) return `${negative ? '-' : ''}${digits}`
  const splitAt = digits.length - scale
  return `${negative ? '-' : ''}${digits.slice(0, splitAt)}.${digits.slice(splitAt)}`
}

function alignScale(value: DecimalValue, scale: number): bigint {
  return value.coefficient * powerOfTen(scale - value.scale)
}

function add(left: DecimalValue, right: DecimalValue): DecimalValue {
  const scale = Math.max(left.scale, right.scale)
  return {
    coefficient: alignScale(left, scale) + alignScale(right, scale),
    scale,
  }
}

function subtract(left: DecimalValue, right: DecimalValue): DecimalValue {
  return add(left, { coefficient: -right.coefficient, scale: right.scale })
}

function multiply(left: DecimalValue, right: DecimalValue): DecimalValue {
  return {
    coefficient: left.coefficient * right.coefficient,
    scale: left.scale + right.scale,
  }
}

function roundHalfUp(value: DecimalValue, scale: number): DecimalValue {
  if (value.scale <= scale) {
    return {
      coefficient: alignScale(value, scale),
      scale,
    }
  }

  const divisor = powerOfTen(value.scale - scale)
  let quotient = value.coefficient / divisor
  const remainder = value.coefficient % divisor
  const absoluteRemainder = remainder < 0n ? -remainder : remainder
  if (absoluteRemainder * 2n >= divisor) {
    quotient += value.coefficient < 0n ? -1n : 1n
  }
  return { coefficient: quotient, scale }
}

export function calculatePayoutFee(amount: string, feeRate: string, fixedFee: string): PayoutFeeEstimate {
  const amountValue = parseDecimal(amount)
  const percentageFee = roundHalfUp(multiply(amountValue, parseDecimal(feeRate)), 2)
  const totalFee = add(percentageFee, parseDecimal(fixedFee))
  const netAmount = subtract(amountValue, totalFee)

  return {
    percentageFee: toDecimalString(percentageFee),
    totalFee: toDecimalString(totalFee),
    netAmount: toDecimalString(netAmount),
  }
}

export function compareDecimalAmounts(left: string, right: string): number {
  const difference = subtract(parseDecimal(left), parseDecimal(right)).coefficient
  return difference < 0n ? -1 : difference > 0n ? 1 : 0
}

export function calculatePayoutAmountFromNet(netAmount: string, feeRate: string, fixedFee: string): string | null {
  try {
    const net = parseDecimal(netAmount)
    const rate = parseDecimal(feeRate)
    const fixed = parseDecimal(fixedFee)
    const rateDivisor = powerOfTen(rate.scale)
    if (net.coefficient <= 0n || fixed.coefficient < 0n || rate.coefficient < 0n || rate.coefficient >= rateDivisor) {
      return null
    }
    const netCents = roundHalfUp(net, 2)
    const fixedCents = roundHalfUp(fixed, 2)
    if (subtract(net, netCents).coefficient !== 0n || subtract(fixed, fixedCents).coefficient !== 0n) {
      return null
    }

    // Invert (net + fixed) / (1 - rate), rounding cents HALF_UP, then verify the forward fee calculation.
    const numerator = (netCents.coefficient + fixedCents.coefficient) * rateDivisor
    const denominator = rateDivisor - rate.coefficient
    const grossCents = (2n * numerator + denominator) / (2n * denominator)
    const amount = toDecimalString({ coefficient: grossCents, scale: 2 })
    return compareDecimalAmounts(calculatePayoutFee(amount, feeRate, fixedFee).netAmount, netAmount) === 0
      ? amount
      : null
  } catch {
    return null
  }
}

export function formatDecimalAmount(value: string): string {
  const rounded = roundHalfUp(parseDecimal(value), 2)
  const negative = rounded.coefficient < 0n
  const digits = (negative ? -rounded.coefficient : rounded.coefficient).toString().padStart(3, '0')
  const whole = digits.slice(0, -2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  const fraction = digits.slice(-2)
  return `${negative ? '-' : ''}${whole}.${fraction}`
}

export function formatDecimalPercent(rate: string): string {
  const percent = roundHalfUp(multiply(parseDecimal(rate), parseDecimal('100')), 2)
  return `${toDecimalString(percent).replace(/\.?0+$/, '')}%`
}

export function isZeroDecimal(value: string): boolean {
  return parseDecimal(value).coefficient === 0n
}

export function isPositiveMultipleOf(value: string, step: string): boolean {
  try {
    const amount = parseDecimal(value)
    const increment = parseDecimal(step)
    const scale = Math.max(amount.scale, increment.scale)
    return amount.coefficient > 0n && increment.coefficient > 0n &&
      alignScale(amount, scale) % alignScale(increment, scale) === 0n
  } catch {
    return false
  }
}
